import { Clerk } from '@clerk/clerk-js/no-rhc'
import {
  createContext,
  createSignal,
  onCleanup,
  onMount,
  Show,
  useContext,
  type Accessor,
  type JSX,
  type ParentProps
} from 'solid-js'

// Minimal Solid bindings for @clerk/clerk-js. The `no-rhc` build skips remotely
// hosted code (wallet SDKs, Cloudflare captcha script), which our custom
// password sign-in form does not need.

type ClerkUser = Clerk['user']
type ClerkSession = Clerk['session']
type GetTokenOptions = Parameters<NonNullable<ClerkSession>['getToken']>[0]

interface ClerkState {
  readonly loaded: boolean
  readonly isSignedIn: boolean
  readonly user: ClerkUser
  readonly session: ClerkSession
}

interface ClerkContextValue {
  readonly clerk: Clerk | undefined
  readonly state: Accessor<ClerkState>
  readonly loadError: Accessor<unknown>
  readonly load: () => void
}

export interface UseAuthReturn {
  readonly isLoaded: () => boolean
  /** `undefined` until Clerk has loaded. */
  readonly isSignedIn: () => boolean | undefined
  /** Throws `ClerkOfflineError` when the browser is offline (Clerk Core 3). */
  readonly getToken: (options?: GetTokenOptions) => Promise<string | null>
}

export interface UseClerkLoadErrorReturn {
  /** The error from the last failed `clerk.load()`, `undefined` otherwise. */
  readonly error: Accessor<unknown>
  /** Clears the error and calls `clerk.load()` again. */
  readonly retry: () => void
}

export interface UseUserReturn {
  readonly isLoaded: () => boolean
  readonly user: () => ClerkUser
}

const ClerkContext = createContext<ClerkContextValue>()

const readState = (clerk: Clerk): ClerkState => ({
  loaded: clerk.loaded,
  isSignedIn: clerk.isSignedIn,
  user: clerk.user,
  session: clerk.session
})

const NOT_LOADED: ClerkState = { loaded: false, isSignedIn: false, user: undefined, session: undefined }

// One instance per page: a provider remount (hot reload in dev) reuses it, so a
// second Clerk never starts and `window.Clerk` stays the live one.
const getClerk = (publishableKey: string): Clerk => {
  if (window.Clerk?.publishableKey !== publishableKey) {
    window.Clerk = new Clerk(publishableKey)
  }
  return window.Clerk
}

export const ClerkProvider = (props: ParentProps<{ publishableKey: string }>): JSX.Element => {
  // The key is read once, like the official SDKs. The constructor throws on a
  // malformed key, and `load` reports that error like a failed load.
  let clerk: Clerk | undefined
  let createError: unknown
  try {
    clerk = getClerk(props.publishableKey)
  } catch (error) {
    createError = error
  }

  // Clerk mutates its resources in place, so every emission must notify.
  const [state, setState] = createSignal(clerk ? readState(clerk) : NOT_LOADED, { equals: false })
  const sync = (): void => {
    if (clerk) setState(readState(clerk))
  }

  const [loadError, setLoadError] = createSignal<unknown>()
  const fail = (error: unknown): void => {
    console.error('Failed to load Clerk:', error)
    setLoadError(() => error ?? new Error('Failed to load Clerk'))
  }
  const load = (): void => {
    setLoadError(undefined)
    if (!clerk) {
      fail(createError)
      return
    }
    clerk.load().then(sync).catch(fail)
  }

  if (clerk) onCleanup(clerk.addListener(sync))

  onMount(load)

  return <ClerkContext.Provider value={{ clerk, state, loadError, load }}>{props.children}</ClerkContext.Provider>
}

const useClerkContext = (): ClerkContextValue => {
  const context = useContext(ClerkContext)
  if (!context) {
    throw new Error('Clerk hooks must be used within a ClerkProvider')
  }
  return context
}

export const useClerk = (): Accessor<Clerk> => {
  const { clerk } = useClerkContext()
  return () => {
    if (!clerk) {
      throw new Error('Clerk failed to start; check the publishable key')
    }
    return clerk
  }
}

export const useAuth = (): UseAuthReturn => {
  const { state } = useClerkContext()
  return {
    isLoaded: () => state().loaded,
    isSignedIn: () => (state().loaded ? state().isSignedIn : undefined),
    getToken: async (options) => (await state().session?.getToken(options)) ?? null
  }
}

export const useClerkLoadError = (): UseClerkLoadErrorReturn => {
  const { loadError, load } = useClerkContext()
  return { error: loadError, retry: load }
}

export const useUser = (): UseUserReturn => {
  const { state } = useClerkContext()
  return {
    isLoaded: () => state().loaded,
    user: () => state().user
  }
}

export const SignedIn = (props: ParentProps): JSX.Element => {
  const { state } = useClerkContext()
  return <Show when={state().loaded && state().isSignedIn}>{props.children}</Show>
}

export const SignedOut = (props: ParentProps): JSX.Element => {
  const { state } = useClerkContext()
  return <Show when={state().loaded && !state().isSignedIn}>{props.children}</Show>
}
